'use client'
import { cn } from '@/utilities/ui'
import { Post } from '@payload-types'
import React, { FC, useState, useEffect, useRef } from 'react'
import { Card } from '../card'
import { useRouter, usePathname, useSearchParams } from 'next/navigation'
import Pagination from '../pagination'

interface NewsListProps {
  className?: string
  posts: Post[]
  categorySlug?: string
  tagSlug?: string
  authorId?: string
  initialPage?: number
  totalPages?: number
}

const PAGE_SIZE = 10

const NewsList: FC<NewsListProps> = ({
  className,
  posts: initialPosts,
  categorySlug,
  tagSlug,
  authorId,
  initialPage = 1,
  totalPages: initialTotalPages,
}) => {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const urlPage = Number(searchParams.get('page')) || initialPage

  // Keep track of if we're currently navigating to avoid duplicate requests
  const isNavigating = useRef(false)

  // State
  const [currentPage, setCurrentPage] = useState(urlPage)
  const [posts, setPosts] = useState<Post[]>(initialPosts)
  const [loading, setLoading] = useState(false)
  const [totalPages, setTotalPages] = useState(
    initialTotalPages || Math.ceil(initialPosts.length / PAGE_SIZE),
  )

  // Only fetch when initialPage updates from URL (server-side)
  // Don't re-fetch on client-side navigation
  const didInitialMount = useRef(false)
  useEffect(() => {
    if (!didInitialMount.current) {
      didInitialMount.current = true
      return
    }

    // This will run on server-component re-render only
    setCurrentPage(urlPage)

    if (!categorySlug && !tagSlug && !authorId) {
      // Client-side pagination
      const startIndex = (urlPage - 1) * PAGE_SIZE
      const endIndex = startIndex + PAGE_SIZE
      setPosts(initialPosts.slice(startIndex, endIndex))
    }
  }, [urlPage, initialPosts, categorySlug, tagSlug, authorId])

  // Handle page change without triggering new data fetches
  const handlePageChange = async (page: number) => {
    if (page === currentPage || isNavigating.current) return

    isNavigating.current = true
    setLoading(true)
    setCurrentPage(page)

    try {
      if (categorySlug) {
        // Fetch data from category API
        const response = await fetch(
          `/api/posts/category/${categorySlug}?page=${page}&limit=${PAGE_SIZE}`,
        )
        const data = await response.json()

        if (data.docs) {
          setPosts(data.docs)
          setTotalPages(Math.ceil(data.totalDocs / PAGE_SIZE))
        }
      } else if (tagSlug) {
        // Fetch data from tag API
        const response = await fetch(`/api/posts/tag/${tagSlug}?page=${page}&limit=${PAGE_SIZE}`)
        const data = await response.json()

        if (data.docs) {
          setPosts(data.docs)
          setTotalPages(Math.ceil(data.totalDocs / PAGE_SIZE))
        }
      } else if (authorId) {
        // Fetch data from yazarlar API
        const response = await fetch(
          `/api/posts/yazarlar/${authorId}?page=${page}&limit=${PAGE_SIZE}`,
        )
        const data = await response.json()

        if (data.docs) {
          setPosts(data.docs)
          setTotalPages(Math.ceil(data.totalDocs / PAGE_SIZE))
        }
      } else {
        // Client-side pagination
        const startIndex = (page - 1) * PAGE_SIZE
        const endIndex = startIndex + PAGE_SIZE
        setPosts(initialPosts.slice(startIndex, endIndex))
      }

      // Update URL without triggering a navigation
      const params = new URLSearchParams(searchParams.toString())
      params.set('page', page.toString())

      // Use history.replaceState to update URL without navigation
      window.history.replaceState({}, '', `${pathname}?${params.toString()}`)
    } catch (error) {
      console.error('Error changing page:', error)
    } finally {
      setLoading(false)
      isNavigating.current = false
    }
  }

  return (
    <div className={cn('space-y-6', className)}>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 min-h-[500px]">
        {loading ? (
          // Loading skeleton
          Array.from({ length: PAGE_SIZE }).map((_, index) => (
            <div key={index} className="bg-gray-100 animate-pulse h-64 rounded-lg"></div>
          ))
        ) : posts.length > 0 ? (
          posts.map((post) => (
            <Card key={post.id} doc={post} variant="primary" showCategories showDate />
          ))
        ) : (
          <div className="col-span-full text-center py-10">
            <p className="text-gray-500">Haber Bulunumadı</p>
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          loading={loading}
        />
      )}
    </div>
  )
}

export default NewsList
